import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Eye, EyeOff, Key, AlertCircle, Check, HardDrive, ShieldCheck, Users, Trash2, Plus, Clock, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function Settings() {
  const { user, updateUser, impersonate, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [apiKeyPreference, setApiKeyPreference] = useState(user?.api_key_preference || "emergent");
  const [personalKey, setPersonalKey] = useState(user?.personal_openai_key || "");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showApiConfig, setShowApiConfig] = useState(
    () => localStorage.getItem('show_api_config') === 'true'
  );
  const [storageStats, setStorageStats] = useState(null);
  const [storageLoading, setStorageLoading] = useState(true);

  // Password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const changePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) return setPasswordMsg({ ok: false, text: "New passwords do not match" });
    if (newPassword.length < 8) return setPasswordMsg({ ok: false, text: "Password must be at least 8 characters" });
    setPasswordSaving(true);
    try {
      if (isImpersonating) {
        const adminToken = localStorage.getItem('admin_original_token');
        await axios.put(`${API}/admin/users/${user.id}/password`, { newPassword }, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
      } else {
        await axios.put(`${API}/auth/password`, { currentPassword, newPassword });
      }
      setPasswordMsg({ ok: true, text: "Password updated" });
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (e) {
      setPasswordMsg({ ok: false, text: e.response?.data?.detail || "Failed to update password" });
    } finally {
      setPasswordSaving(false);
    }
  };

  // Admin state
  const isAdmin = user?.role === "admin";
  const isPopBoutique = user?.role === "pop_boutique";
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [togglingUser, setTogglingUser] = useState(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);

  const handleResetPassword = async (userId) => {
    if (resetPasswordValue.length < 8) return toast.error("Password must be at least 8 characters");
    setResettingPassword(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/password`, { newPassword: resetPasswordValue });
      toast.success("Password reset");
      setResetPasswordUserId(null);
      setResetPasswordValue("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };
  const [loginActivity, setLoginActivity] = useState([]);
  const [activityExpanded, setActivityExpanded] = useState(null); // user id with expanded history

  useEffect(() => {
    axios.get(`${API}/storage-stats`)
      .then(({ data }) => setStorageStats(data))
      .catch(() => {})
      .finally(() => setStorageLoading(false));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/admin/allowed-emails`).then(({ data }) => setAllowedEmails(data)).catch(() => {});
    axios.get(`${API}/admin/users`).then(({ data }) => setAdminUsers(data)).catch(() => {});
    axios.get(`${API}/admin/login-activity`, { withCredentials: true }).then(({ data }) => setLoginActivity(data)).catch(() => {});
  }, [isAdmin]);

  const addEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAddingEmail(true);
    try {
      const { data } = await axios.post(`${API}/admin/allowed-emails`, { email: newEmail.trim() });
      setAllowedEmails((prev) => [data, ...prev]);
      setNewEmail("");
      toast.success("Email added to whitelist");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add email");
    } finally {
      setAddingEmail(false);
    }
  };

  const removeEmail = async (email) => {
    try {
      await axios.delete(`${API}/admin/allowed-emails/${encodeURIComponent(email)}`);
      setAllowedEmails((prev) => prev.filter((e) => e.email !== email));
      toast.success("Removed from whitelist");
    } catch {
      toast.error("Failed to remove email");
    }
  };

  const toggleDeleteBank = async (userId, currentValue) => {
    setTogglingUser(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/permissions`, { can_delete_bank: !currentValue });
      setAdminUsers((prev) => prev.map((u) => u.id === userId ? { ...u, can_delete_bank: currentValue ? 0 : 1 } : u));
      toast.success("Permission updated");
    } catch {
      toast.error("Failed to update permission");
    } finally {
      setTogglingUser(null);
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/permissions`, { role: newRole });
      setAdminUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role: newRole } : u));
      toast.success("Role updated");
    } catch {
      toast.error("Failed to update role");
    }
  };

  const handleImpersonate = async (userId) => {
    const result = await impersonate(userId);
    if (result.success) {
      toast.success(`Now impersonating ${result.name}`);
      navigate("/");
    } else {
      toast.error(result.error || "Failed to impersonate user");
    }
  };

  const toggleApiConfig = () => {
    const next = !showApiConfig;
    setShowApiConfig(next);
    localStorage.setItem('show_api_config', String(next));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings`, {
        api_key_preference: apiKeyPreference,
        personal_openai_key: apiKeyPreference === "personal" ? personalKey : null
      }, { withCredentials: true });
      updateUser(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl pb-20 md:pb-0" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
          Settings
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Manage your API keys and preferences
        </p>
      </div>

      {/* API Key Section — hidden for restricted roles */}
      {!isPopBoutique && <div className="border border-gray-200">
        <div className="flex items-center gap-3 p-6">
          <div className="w-10 h-10 bg-black text-white flex items-center justify-center flex-shrink-0">
            <Key size={18} />
          </div>
          <div className="flex-1">
            <h2 className="font-heading font-bold">API Key Configuration</h2>
            <p className="text-sm text-gray-500">Choose how to connect to OpenAI</p>
          </div>
          <Switch checked={showApiConfig} onCheckedChange={toggleApiConfig} />
        </div>

        {showApiConfig && <div className="px-6 pb-6 space-y-6 border-t border-gray-200 pt-6">

        <RadioGroup value={apiKeyPreference} onValueChange={setApiKeyPreference} className="space-y-4">
          <div className="flex items-start space-x-3 p-4 border border-gray-200 hover:border-gray-400 transition-colors cursor-pointer">
            <RadioGroupItem value="emergent" id="emergent" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="emergent" className="font-medium cursor-pointer">
                Emergent Universal Key
              </Label>
              <p className="text-sm text-gray-500 mt-1">
                Use the pre-configured key. Credits are deducted from your Emergent balance.
              </p>
              <div className="mt-2 p-2 bg-green-50 border border-green-200 text-green-700 text-xs flex items-center gap-2">
                <Check size={12} />
                Ready to use - no setup required
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 border border-gray-200 hover:border-gray-400 transition-colors cursor-pointer">
            <RadioGroupItem value="personal" id="personal" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="personal" className="font-medium cursor-pointer">
                Personal OpenAI Key
              </Label>
              <p className="text-sm text-gray-500 mt-1">
                Use your own OpenAI API key. Billing goes directly to your OpenAI account.
              </p>
            </div>
          </div>
        </RadioGroup>

        {apiKeyPreference === "personal" && (
          <div className="space-y-3 p-4 bg-gray-50 border border-gray-200">
            <Label className="text-xs uppercase tracking-widest text-gray-500">
              Your OpenAI API Key
            </Label>
            <div className="relative">
              <Input
                type={showKey ? "text" : "password"}
                value={personalKey}
                onChange={(e) => setPersonalKey(e.target.value)}
                placeholder="sk-..."
                className="rounded-none border-gray-300 pr-10"
                data-testid="personal-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {!personalKey && (
              <div className="p-2 bg-yellow-50 border border-yellow-200 text-yellow-700 text-xs flex items-start gap-2">
                <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                <span>
                  You need to enter your API key to use this option.
                  Get one at <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">platform.openai.com</a>
                </span>
              </div>
            )}
          </div>
        )}

        <Button
          onClick={handleSave}
          disabled={saving || (apiKeyPreference === "personal" && !personalKey)}
          className="w-full rounded-none bg-black text-white hover:bg-gray-800"
          data-testid="save-settings"
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
        </div>}
      </div>}

      {/* Account Info */}
      <div className="border border-gray-200 p-6 mt-6">
        <h2 className="font-heading font-bold mb-4">Account Information</h2>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Name</span>
            <span className="font-medium">{user?.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Email</span>
            <span className="font-medium">{user?.email}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-500">Role</span>
            <span className="font-medium capitalize">{user?.role === 'user' ? 'Rad and Nov' : user?.role}</span>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="border border-gray-200 mt-6">
        <div className="flex items-center gap-3 p-6 border-b border-gray-200">
          <div className="w-10 h-10 bg-black text-white flex items-center justify-center flex-shrink-0">
            <Key size={18} />
          </div>
          <div>
            <h2 className="font-heading font-bold">Change Password</h2>
            <p className="text-sm text-gray-500">Update your account password</p>
          </div>
        </div>
        <form onSubmit={changePassword} className="p-6 space-y-3">
          {!isImpersonating && (
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-widest mb-1 block">Current password</label>
              <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required className="rounded-none border-gray-300" />
            </div>
          )}
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest mb-1 block">New password</label>
            <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required className="rounded-none border-gray-300" />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-widest mb-1 block">Confirm new password</label>
            <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required className="rounded-none border-gray-300" />
          </div>
          {passwordMsg && (
            <p className={`text-sm ${passwordMsg.ok ? "text-green-600" : "text-red-600"}`}>{passwordMsg.text}</p>
          )}
          <Button type="submit" disabled={passwordSaving} className="w-full rounded-none bg-black text-white hover:bg-gray-800">
            {passwordSaving ? "Updating…" : "Update Password"}
          </Button>
        </form>
      </div>

      {/* Admin: Registration Whitelist */}
      {isAdmin && (
        <div className="border border-gray-200 mt-6">
          <div className="flex items-center gap-3 p-6 border-b border-gray-200">
            <div className="w-10 h-10 bg-black text-white flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={18} />
            </div>
            <div>
              <h2 className="font-heading font-bold">Registration Whitelist</h2>
              <p className="text-sm text-gray-500">Only these email addresses can register</p>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <form onSubmit={addEmail} className="flex gap-2">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="email@example.com"
                className="rounded-none border-gray-300 flex-1"
              />
              <Button type="submit" disabled={addingEmail || !newEmail.trim()} className="rounded-none bg-black text-white hover:bg-gray-800 flex-shrink-0">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </form>
            {allowedEmails.length === 0 ? (
              <p className="text-sm text-gray-400">No emails in whitelist.</p>
            ) : (
              <div className="space-y-2">
                {allowedEmails.map((e) => (
                  <div key={e.email} className="flex items-center justify-between py-2 px-3 bg-gray-50 border border-gray-200">
                    <span className="text-sm font-medium">{e.email}</span>
                    <button onClick={() => removeEmail(e.email)} className="text-red-400 hover:text-red-600 transition-colors" title="Remove">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin: User Permissions */}
      {isAdmin && (
        <div className="border border-gray-200 mt-6">
          <div className="flex items-center gap-3 p-6 border-b border-gray-200">
            <div className="w-10 h-10 bg-black text-white flex items-center justify-center flex-shrink-0">
              <Users size={18} />
            </div>
            <div>
              <h2 className="font-heading font-bold">User Permissions</h2>
              <p className="text-sm text-gray-500">Control what each user can do in Bank</p>
            </div>
          </div>
          <div className="p-6">
            {adminUsers.length === 0 ? (
              <p className="text-sm text-gray-400">No users found.</p>
            ) : (
              <div className="space-y-3">
                {adminUsers.map((u) => (
                  <div key={u.id} className="py-3 px-4 border border-gray-200">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{u.name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                      {u.role === "admin" ? (
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 flex-shrink-0">Admin</span>
                      ) : (
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Delete Bank</span>
                            <Switch
                              checked={u.can_delete_bank === 1}
                              disabled={togglingUser === u.id}
                              onCheckedChange={() => toggleDeleteBank(u.id, u.can_delete_bank === 1)}
                            />
                          </div>
                          <Select value={u.role} onValueChange={(val) => changeRole(u.id, val)}>
                            <SelectTrigger className="rounded-none border-gray-300 bg-white text-black h-8 text-xs w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">Rad and Nov</SelectItem>
                              <SelectItem value="affinity">Affinity</SelectItem>
                              <SelectItem value="pop_boutique">Pop Boutique</SelectItem>
                              <SelectItem value="pop_waitlist">Pop Waitlist</SelectItem>
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => handleImpersonate(u.id)}
                            className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 hover:border-black hover:bg-gray-50 transition-colors"
                            title={`Impersonate ${u.name}`}
                          >
                            <UserCheck size={13} />
                            Impersonate
                          </button>
                          <button
                            onClick={() => { setResetPasswordUserId(resetPasswordUserId === u.id ? null : u.id); setResetPasswordValue(""); }}
                            className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 hover:border-black hover:bg-gray-50 transition-colors"
                          >
                            <Key size={13} />
                            Reset Password
                          </button>
                        </div>
                      )}
                    </div>
                    {resetPasswordUserId === u.id && (
                      <div className="flex items-center gap-2 mt-3">
                        <div className="relative flex-1">
                          <Input
                            type={resetPasswordVisible ? "text" : "password"}
                            placeholder="New password"
                            value={resetPasswordValue}
                            onChange={e => setResetPasswordValue(e.target.value)}
                            className="rounded-none border-gray-300 h-8 text-xs pr-8"
                          />
                          <button
                            type="button"
                            onClick={() => setResetPasswordVisible(v => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {resetPasswordVisible ? <EyeOff size={13} /> : <Eye size={13} />}
                          </button>
                        </div>
                        <button
                          onClick={() => handleResetPassword(u.id)}
                          disabled={resettingPassword}
                          className="text-xs px-3 py-1.5 bg-black text-white hover:bg-gray-800 disabled:opacity-50 flex-shrink-0"
                        >
                          {resettingPassword ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={() => { setResetPasswordUserId(null); setResetPasswordValue(""); }}
                          className="text-xs px-3 py-1.5 border border-gray-300 hover:bg-gray-50 flex-shrink-0"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Login Activity — admin only */}
      {isAdmin && (
        <div className="border border-gray-200 mt-6">
          <div className="flex items-center gap-3 p-6 border-b border-gray-200">
            <div className="w-10 h-10 bg-black text-white flex items-center justify-center flex-shrink-0">
              <Clock size={18} />
            </div>
            <div>
              <h2 className="font-heading font-bold">Login Activity</h2>
              <p className="text-sm text-gray-500">Recent sign-ins per user</p>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {loginActivity.length === 0 ? (
              <p className="text-sm text-gray-400">No login data yet.</p>
            ) : (
              loginActivity.map((u) => (
                <div key={u.id} className="border border-gray-200">
                  <button
                    onClick={() => setActivityExpanded(activityExpanded === u.id ? null : u.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 bg-black text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{u.name}</p>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {u.last_login_at ? (
                        <p className="text-xs text-gray-500">
                          {new Date(u.last_login_at).toLocaleString()}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-300">Never logged in</p>
                      )}
                      <p className="text-[10px] text-gray-400 mt-0.5">{u.login_history?.length || 0} recorded session{u.login_history?.length !== 1 ? "s" : ""}</p>
                    </div>
                  </button>

                  {activityExpanded === u.id && u.login_history?.length > 0 && (
                    <div className="border-t border-gray-100 divide-y divide-gray-100">
                      {[...u.login_history].reverse().map((entry, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-2 bg-gray-50 text-xs">
                          <span className="text-gray-500">{new Date(entry.timestamp).toLocaleString()}</span>
                          <span className="text-gray-400 font-mono">{entry.ip}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Storage Stats */}
      <div className="border border-gray-200 p-6 mt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-black text-white flex items-center justify-center flex-shrink-0">
            <HardDrive size={18} />
          </div>
          <div>
            <h2 className="font-heading font-bold">Storage Usage</h2>
            <p className="text-sm text-gray-500">Cloudflare R2 image storage</p>
          </div>
        </div>

        {storageLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
            Calculating...
          </div>
        ) : storageStats ? (
          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Images</span>
              <span className="font-medium">{storageStats.image_count}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-500">Total files (incl. print-ready)</span>
              <span className="font-medium">{storageStats.object_count}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500">Storage used</span>
              <span className="font-medium">{formatBytes(storageStats.total_bytes)}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Unable to load storage stats.</p>
        )}
      </div>
    </div>
  );
}
